import type { ReactNode } from 'react';
import { AlertTriangle, Inbox } from 'lucide-react';
import { sdkScriptUrl } from '../api.js';
import { useActivePid } from './Layout.js';

/*
 * BU BİLEŞEN ROUTER'A BAĞLANMAZ (`useNavigate` YOK).
 *
 * EmptyState 15 sayfada kullanılır ve DOM testlerinin çoğu o sayfaları Router'sız
 * render eder; buraya bir router kancası koymak, ilgisiz 10+ testi "useNavigate()
 * may be used only in the context of a Router" ile kırardı. Gezinme ÇAĞIRANIN
 * işidir (`onAction`); §72.5/3'ün `<a href>` → SPA geçişi düzeltmesi bu yüzden
 * burada değil, çağıranlarda yapıldı.
 */

/**
 * §13 son paragraf — "Boş durumlar (veri yokken) MUTLAKA tasarlanmalı:
 * 'SDK'yı ekleyin → 5 dakikada ilk olaylar'".
 *
 * Görsel dil referans panelin boş durumu ile birebir: 48px yuvarlak nötr zemin
 * içinde `Inbox` ikonu, 15px koyu gri başlık, 13px açık gri açıklama.
 *
 * Varsayılan metinler tam da bu kurulum mesajıdır; başlık/açıklama/aksiyon
 * prop'ları verilerek "bu filtreye uyan oturum yok" gibi yerel boşluklarda da
 * yeniden kullanılır.
 */

/** §13 varsayılan kurulum çağrısı — tek yerde tanımlı, testlerde de aranabilir. */
export const SDK_EMPTY_TITLE = "SDK'yı ekleyin → 5 dakikada ilk olaylar";
export const SDK_EMPTY_DESCRIPTION =
  'Henüz veri yok. Sitenizin <head> bölümüne tek satırlık izleyici etiketini ekleyin; ' +
  'ilk sayfa görüntülemeleri birkaç dakika içinde burada belirir.';

export interface EmptyStateProps {
  title?: ReactNode;
  description?: ReactNode;
  /** Birincil eylem etiketi — verilmezse buton çizilmez. */
  actionLabel?: string;
  onAction?: () => void;
  /** Buton yerine bağlantı gerekiyorsa (kurulum dokümanı gibi). */
  actionHref?: string;
  /** Başlığın üstündeki dekoratif işaret; verilmezse lucide `Inbox` kullanılır. */
  icon?: ReactNode;
  /** Kurulum kod parçası gibi ek içerik. */
  children?: ReactNode;
  /** Kart içinde dar alanlarda kullanılacak sıkışık sürüm — çerçeve çizilmez. */
  compact?: boolean;
  className?: string;
}

/**
 * §72.5/2 — kurulum etiketi GERÇEK proje ve GERÇEK sunucu adresiyle üretilir.
 *
 * Eskiden burada sabit bir dize vardı (`localhost:4000` + `prj_demo`). Boş durum tam
 * da SDK'sı hiç kurulmamış müşterinin gördüğü ekrandır; oradaki etiketi kopyalayan
 * kişi kendi sitesine BAŞKA bir projenin pid'ini ve erişemeyeceği bir adresi
 * yapıştırıyor, hiç veri gelmiyor ve hatanın nerede olduğunu göremiyordu. Yani
 * ürünün "5 dakikada ilk olaylar" vaadini bozan tek satır buydu.
 *
 * `sdkScriptUrl()` sihirbazın kullandığı ile AYNI kaynaktır (§72.3 tek kaynak kuralı).
 */
export function sdkSnippet(pid: string): string {
  return `<script src="${sdkScriptUrl()}" data-pid="${pid}" async></script>`;
}

export default function EmptyState({
  title = SDK_EMPTY_TITLE,
  description = SDK_EMPTY_DESCRIPTION,
  actionLabel,
  onAction,
  actionHref,
  icon,
  children,
  compact = false,
  className = '',
}: EmptyStateProps) {
  // Varsayılan (SDK kurulum) durumunda kod parçasını da göster — kullanıcı kopyalayıp yapıştırsın.
  const showSnippet = children === undefined && title === SDK_EMPTY_TITLE && !compact;
  // §72.5/2 — etiket AKTİF projenin pid'ini taşır; sabit 'prj_demo' yazmak yasak.
  const pid = useActivePid();

  // Sıkışık sürüm bir kartın içinde yaşar; kendi çerçevesini çizmez.
  const shell = compact
    ? 'px-4 py-10'
    : 'rounded-2xl border border-[#e5e7eb] bg-white px-6 py-14';

  return (
    <div className={`flex flex-col items-center justify-center text-center ${shell} ${className}`}>
      <div
        aria-hidden="true"
        className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-[#f9fafb] text-[15px] text-[#9ca3af]"
      >
        {icon ?? <Inbox className="h-6 w-6 text-[#9ca3af]" />}
      </div>

      <h3 className="text-[15px] font-semibold text-[#374151]">{title}</h3>

      {description !== undefined ? (
        <p className="mt-1.5 max-w-md text-[13px] text-[#9ca3af]">{description}</p>
      ) : null}

      {showSnippet ? (
        <pre
          data-testid="bos-durum-snippet"
          className="mt-4 max-w-full overflow-x-auto rounded-xl bg-[#171719] px-4 py-3 text-left font-mono text-[12px] text-[#e5e7eb]"
        >
          {sdkSnippet(pid)}
        </pre>
      ) : null}

      {children}

      {actionLabel !== undefined ? (
        actionHref !== undefined ? (
          <a className="btn-primary mt-5" href={actionHref}>
            {actionLabel}
          </a>
        ) : (
          <button type="button" className="btn-primary mt-5" onClick={onAction}>
            {actionLabel}
          </button>
        )
      ) : null}
    </div>
  );
}

/**
 * Hata durumu — boş durumun kardeşi. API hatalarında sayfaların tek tip göstermesi için.
 */
export function ErrorState({
  title = 'Veri alınamadı',
  description,
  onRetry,
}: {
  title?: string;
  description?: ReactNode;
  onRetry?: () => void;
}) {
  return (
    <div className="flex flex-col items-center justify-center rounded-2xl border border-[#fecaca] bg-[#fef2f2] px-6 py-12 text-center">
      <div
        aria-hidden="true"
        className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-white"
      >
        <AlertTriangle className="h-6 w-6 text-red-500" />
      </div>
      <h3 className="text-[15px] font-semibold text-red-800">{title}</h3>
      {description !== undefined ? (
        <p className="mt-1.5 max-w-md text-[13px] text-red-700">{description}</p>
      ) : null}
      {onRetry !== undefined ? (
        <button type="button" className="btn-ghost mt-5" onClick={onRetry}>
          Tekrar dene
        </button>
      ) : null}
    </div>
  );
}
